import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { flushOfflineQueue, syncProducts, syncCustomers, isProductsDirty, markProductsClean, isProductSyncStale } from '@/lib/offlineSync';
import { getPendingQueueItems } from '@/lib/offlineDB';

const _PRODUCTS_SYNC_COOLDOWN = 60 * 60 * 1000; // 60 min — Realtime broadcast (create/update/delete/merge) now covers live sync; this is a safety-net backstop only

function _isProductSyncStale() {
  return isProductSyncStale(_PRODUCTS_SYNC_COOLDOWN);
}

const OfflineContext = createContext(null);

function _shouldSync() {
  const token = localStorage.getItem('kiranaflow_token');
  if (!token) return false;
  try {
    const u = JSON.parse(localStorage.getItem('kiranaflow_user') || '{}');
    if (u.role === 'platform_admin') return false;
  } catch { return false; }
  return true;
}

export function OfflineProvider({ children }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const syncingRef = useRef(false); // guard against concurrent sync calls

  const refreshPendingCount = useCallback(async () => {
    try {
      const pending = await getPendingQueueItems();
      setPendingCount(pending.length);
    } catch {
      // ignore
    }
  }, []);

  const triggerSync = useCallback(async () => {
    if (syncingRef.current || !navigator.onLine) return;
    if (!_shouldSync()) return;
    syncingRef.current = true;
    setIsSyncing(true);
    try {
      await flushOfflineQueue(({ total, processed }) => {
        setPendingCount(Math.max(0, total - processed));
      });
      const _u = JSON.parse(localStorage.getItem('kiranaflow_user') || '{}');
      if (isProductsDirty() || _isProductSyncStale()) {
        await syncProducts(_u.store_id); // marks the shared sync timestamp internally
        markProductsClean();
      }
      await syncCustomers();
      setLastSyncedAt(Date.now());
    } catch {
      // individual item errors are handled inside flushOfflineQueue
    } finally {
      syncingRef.current = false;
      setIsSyncing(false);
      await refreshPendingCount();
    }
  }, [refreshPendingCount]);

  // Boot: load pending count, then sync if online
  useEffect(() => {
    refreshPendingCount();
    if (navigator.onLine) {
      triggerSync();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Periodic background product sync — a safety-net backstop, not the primary sync
  // path. Catalog writes (create/update/delete/merge) broadcast live over Realtime to
  // POS and StockMaster (see helpers/realtime.py), so this only needs to catch a
  // dropped/missed broadcast, a brand-new tab/device with no subscription history, or
  // pages that don't subscribe (Reports, Admin, etc.). Stamping lastSyncedAt on success
  // lets an already-mounted POS page notice this sync happened and pull the
  // freshly-written IndexedDB data into its live product list, instead of only ever
  // refreshing on its own 30-min gate.
  useEffect(() => {
    const id = setInterval(() => {
      if (navigator.onLine && !syncingRef.current && _shouldSync()) {
        const _u = JSON.parse(localStorage.getItem('kiranaflow_user') || '{}');
        syncProducts(_u.store_id)
          .then(() => setLastSyncedAt(Date.now()))
          .catch(() => {});
      }
    }, _PRODUCTS_SYNC_COOLDOWN);
    return () => clearInterval(id);
  }, []);

  // Online / offline events
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      triggerSync();
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [triggerSync]);

  return (
    <OfflineContext.Provider value={{
      isOnline,
      isSyncing,
      pendingCount,
      lastSyncedAt,
      triggerSync,
      refreshPendingCount,
    }}>
      {children}
    </OfflineContext.Provider>
  );
}

export function useOffline() {
  const ctx = useContext(OfflineContext);
  if (!ctx) throw new Error('useOffline must be used inside OfflineProvider');
  return ctx;
}
